import React, { useEffect, useState } from 'react';
import { ACTIONS_COL_WIDTH, Alert, Badge, Button, Col, Form, Modal, Row, RowActions, Table } from '@/components/ui';
import {
  ResourceRate,
  Vendor,
  VendorResource,
  vendorService,
} from '../../../services/vendorService';
import { currency, shortDate } from '../utils/format';

interface Props {
  show: boolean;
  onHide: () => void;
  vendor: Vendor;
  resource?: VendorResource | null;
  onSaved: () => Promise<void> | void;
}

/** The date input wants yyyy-mm-dd, and must not shift the day while converting. */
const toDateInput = (value?: string | null): string => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

const StatusBadge: React.FC<{ status: ResourceRate['status'] }> = ({ status }) => {
  if (status === 'APPROVED') return <Badge bg="success">Approved</Badge>;
  if (status === 'PENDING') return <Badge bg="warning">Awaiting approval</Badge>;
  if (status === 'REJECTED') return <Badge bg="danger">Rejected</Badge>;
  return <Badge bg="secondary">Superseded</Badge>;
};

/**
 * Rates per service area (HE-M05) and the change-approval workflow (HE-M12).
 *
 * The form sits behind an "Add Rate" button rather than always on screen — the same shape the
 * Other Costs tab in TaskModal uses, so the default view is the history and adding is a
 * deliberate act.
 *
 * "Edit" on an approved rate does NOT mutate it: it pre-fills the form so the change goes
 * through the normal supersede-then-approve path. Editing in place would silently rewrite what
 * work was actually charged at.
 */
const RateModal: React.FC<Props> = ({ show, onHide, vendor, resource, onSaved }) => {
  const [rates, setRates] = useState<ResourceRate[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [serviceAreaId, setServiceAreaId] = useState<string>('');
  const [rate, setRate] = useState<string>('');
  const [effectiveFrom, setEffectiveFrom] = useState<string>('');
  const [effectiveTo, setEffectiveTo] = useState<string>('');
  const [comments, setComments] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!resource) return;
    try {
      setLoading(true);
      setRates(await vendorService.listRatesForResource(resource.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rates');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setRate('');
    setComments('');
    setEffectiveFrom('');
    setEffectiveTo('');
    setServiceAreaId('');
  };

  useEffect(() => {
    if (!show || !resource) return;
    setError(null);
    setFormOpen(false);
    resetForm();
    load();
  }, [show, resource]);

  if (!resource) return null;

  // A rate can only exist where the resource is actually offered.
  const offeredAreas = (resource.serviceAreas ?? []).map((sa) => sa.serviceArea);

  const now = Date.now();
  const existingApproved = rates.find((r) => {
    if (r.status !== 'APPROVED' || String(r.serviceAreaId) !== serviceAreaId) return false;
    if (!r.effectiveTo) return true;
    const to = new Date(r.effectiveTo);
    return !Number.isNaN(to.getTime()) && to.getTime() >= now;
  });
  const willNeedApproval = !!existingApproved;

  const openAdd = () => {
    resetForm();
    setError(null);
    setFormOpen(true);
  };

  /** Pre-fill from an existing rate; the save path is unchanged. */
  const openEdit = (r: ResourceRate) => {
    setServiceAreaId(String(r.serviceAreaId));
    setRate(String(r.rate));
    setEffectiveFrom(toDateInput(r.effectiveFrom));
    setEffectiveTo(toDateInput(r.effectiveTo));
    setComments(r.comments ?? '');
    setError(null);
    setFormOpen(true);
  };

  const handleCancelPending = async (r: ResourceRate) => {
    if (!window.confirm('Withdraw this pending rate change? It will not be recoverable.')) return;
    try {
      setError(null);
      await vendorService.deleteRate(r.id);
      await load();
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel rate change');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseFloat(rate);
    if (!serviceAreaId) {
      setError('Choose a service area.');
      return;
    }
    if (Number.isNaN(parsed) || parsed < 0) {
      setError('Enter a rate of zero or more.');
      return;
    }
    if (effectiveFrom && effectiveTo && effectiveTo < effectiveFrom) {
      setError('Effective To cannot be before Effective From.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await vendorService.upsertRate(resource.id, {
        serviceAreaId: parseInt(serviceAreaId, 10),
        rate: parsed,
        effectiveFrom: effectiveFrom || undefined,
        effectiveTo: effectiveTo || null,
        comments: comments || undefined,
      });
      await load();
      resetForm();
      setFormOpen(false);
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save rate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="2xl">
      <Modal.Header closeButton>
        <Modal.Title>
          Rates — {resource.name} <span className="tw-text-content-muted">({vendor.name})</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {resource.isFixedPrice && (
          <Alert variant="info">
            This is a <strong>fixed-price</strong> resource: the rate below is charged as entered,
            with no markup applied.
          </Alert>
        )}

        <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
          <h6 className="tw-m-0">Rates by service area</h6>
          {!formOpen && (
            <Button variant="primary" size="sm" onClick={openAdd}>
              <i className="bi bi-plus tw-mr-1" />
              Add Rate
            </Button>
          )}
        </div>

        {formOpen && (
          <Form onSubmit={handleSubmit} className="tw-mb-4 tw-p-3 tw-bg-surface-alt tw-rounded-lg">
            <Row>
              <Col md={3}>
                <Form.Group controlId="rate-service-area" className="tw-mb-3">
                  <Form.Label>Service Area *</Form.Label>
                  <Form.Select value={serviceAreaId} onChange={(e) => setServiceAreaId(e.target.value)}>
                    <option value="">Choose…</option>
                    {offeredAreas.map((sa) => (
                      <option key={sa.id} value={sa.id}>
                        {sa.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="rate-amount" className="tw-mb-3">
                  <Form.Label>Rate *</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    step="0.01"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="rate-effective-from" className="tw-mb-3">
                  <Form.Label>Effective From</Form.Label>
                  <Form.Control
                    type="date"
                    value={effectiveFrom}
                    onChange={(e) => setEffectiveFrom(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="rate-effective-to" className="tw-mb-3">
                  <Form.Label>Effective To</Form.Label>
                  <Form.Control
                    type="date"
                    value={effectiveTo}
                    min={effectiveFrom || undefined}
                    onChange={(e) => setEffectiveTo(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group controlId="rate-comments" className="tw-mb-3">
                  <Form.Label>Comments</Form.Label>
                  <Form.Control
                    type="text"
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    placeholder={willNeedApproval ? 'Why is this changing?' : ''}
                  />
                </Form.Group>
              </Col>
            </Row>

            {serviceAreaId && (
              <Alert variant={willNeedApproval ? 'warning' : 'info'} className="tw-mb-3">
                {willNeedApproval ? (
                  <>
                    <strong>This change requires approval.</strong> The current rate is{' '}
                    {currency(existingApproved?.rate)}. It will be held until a different person
                    approves it, and live pricing keeps using the current rate until then.
                  </>
                ) : (
                  <>
                    <strong>This is a first rate</strong> for this resource in this area, so it takes
                    effect immediately. Later changes will need a second person to approve.
                  </>
                )}
              </Alert>
            )}

            <div className="tw-flex tw-justify-end tw-gap-2">
              <Button
                variant="outline-secondary"
                type="button"
                onClick={() => {
                  setFormOpen(false);
                  resetForm();
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={saving}>
                {saving ? 'Saving…' : willNeedApproval ? 'Submit for Approval' : 'Set Rate'}
              </Button>
            </div>
          </Form>
        )}

        <Table responsive className="tw-mb-0">
          <thead>
            <tr>
              <th>Service Area</th>
              <th>Rate</th>
              <th>Effective From</th>
              <th>Effective To</th>
              <th>Status</th>
              <th>Requested By</th>
              <th style={{ width: ACTIONS_COL_WIDTH }} className="tw-text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="tw-text-center tw-py-3">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rates.length === 0 && (
              <tr>
                <td colSpan={7} className="tw-text-center tw-py-4 tw-text-content-muted">
                  <i className="bi bi-cash-stack tw-text-3xl tw-block tw-mb-2" />
                  No rates set for this resource yet.
                  <div>
                    <small>Use &ldquo;Add Rate&rdquo; to set one per service area.</small>
                  </div>
                </td>
              </tr>
            )}
            {!loading &&
              rates.map((r) => (
                <tr key={r.id}>
                  <td>{r.serviceArea?.name ?? r.serviceAreaId}</td>
                  <td>{currency(r.rate)}</td>
                  <td>{shortDate(r.effectiveFrom)}</td>
                  <td>{r.effectiveTo ? shortDate(r.effectiveTo) : '—'}</td>
                  <td>
                    <StatusBadge status={r.status} />
                  </td>
                  <td>{r.requestedBy ? `${r.requestedBy.name} ${r.requestedBy.lastName}` : '—'}</td>
                  <td className="tw-text-right">
                    {/* Superseded and rejected rows are history; there is nothing to act on. */}
                    <RowActions
                      actions={[
                        {
                          kind: 'edit',
                          label: 'Change Rate',
                          onClick: () => openEdit(r),
                          hidden: r.status !== 'APPROVED' && r.status !== 'PENDING',
                        },
                        {
                          kind: 'cancel',
                          label: 'Withdraw Change',
                          onClick: () => handleCancelPending(r),
                          hidden: r.status !== 'PENDING',
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>

        <div className="tw-text-sm tw-text-content-muted tw-mt-3">
          Editing an approved rate does not overwrite it — it submits a change that supersedes it
          once approved, so what the work was charged at stays on the record.
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" type="button" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RateModal;
